import React from 'react';
import { Helmet } from 'react-helmet-async';

interface SchemaProps {
  type: 'organization' | 'faq' | 'service' | 'localBusiness' | 'product' | 'article';
  data?: any;
}

export const Schema: React.FC<SchemaProps> = ({ type, data = {} }) => {
  const getSchemaMarkup = () => {
    const baseUrl = window.location.origin;
    
    switch (type) {
      case 'organization':
        return {
          "@context": "https://schema.org",
          "@type": "Organization",
          "name": "Cognitive Craft Hub",
          "legalName": "Cognitive Craft Hub LLC",
          "url": baseUrl,
          "logo": `${baseUrl}/images/logo.png`,
          "description": "Leading AI solutions company providing artificial intelligence services, machine learning, automation, blockchain technology, and custom software development.",
          "foundingDate": "2023",
          "founders": [
            {
              "@type": "Person",
              "name": "Cognitive Craft Hub Team"
            }
          ],
          "address": [
            {
              "@type": "PostalAddress",
              "streetAddress": "East Lansing",
              "addressLocality": "East Lansing",
              "addressRegion": "MI",
              "addressCountry": "US"
            },
            {
              "@type": "PostalAddress",
              "streetAddress": "Houston",
              "addressLocality": "Houston",
              "addressRegion": "TX",
              "addressCountry": "US"
            }
          ],
          "contactPoint": {
            "@type": "ContactPoint",
            "telephone": "+1-800-AI-CRAFT",
            "contactType": "customer service",
            "availableLanguage": "English"
          },
          "sameAs": [
            "https://linkedin.com/company/cognitive-craft-hub",
            "https://twitter.com/cognitivecraft"
          ],
          "serviceArea": {
            "@type": "Place",
            "name": "United States",
            "geo": {
              "@type": "GeoShape",
              "addressCountry": "US"
            }
          },
          "makesOffer": [
            {
              "@type": "Offer",
              "itemOffered": {
                "@type": "Service",
                "name": "AI Automation Services",
                "description": "Comprehensive AI automation and machine learning solutions for businesses"
              }
            },
            {
              "@type": "Offer",
              "itemOffered": {
                "@type": "Service",
                "name": "Blockchain Development",
                "description": "Smart contracts, DApps, and distributed ledger technology solutions"
              }
            },
            {
              "@type": "Offer",
              "itemOffered": {
                "@type": "Service",
                "name": "Custom Software Development",
                "description": "Tailored software solutions and web application development"
              }
            }
          ]
        };

      case 'localBusiness':
        return {
          "@context": "https://schema.org",
          "@type": "LocalBusiness",
          "@id": `${baseUrl}/#localbusiness`,
          "name": "Cognitive Craft Hub",
          "image": `${baseUrl}/images/logo.png`,
          "description": "AI solutions company serving Michigan, Ohio, Texas, Illinois, and Indiana with artificial intelligence, automation, and blockchain services.",
          "url": baseUrl,
          "telephone": "+1-800-AI-CRAFT",
          "priceRange": "$2000-$100000",
          "address": [
            {
              "@type": "PostalAddress",
              "addressLocality": "East Lansing",
              "addressRegion": "MI",
              "addressCountry": "US"
            },
            {
              "@type": "PostalAddress",
              "addressLocality": "Houston",
              "addressRegion": "TX",
              "addressCountry": "US"
            }
          ],
          "geo": [
            {
              "@type": "GeoCoordinates",
              "latitude": 42.7335,
              "longitude": -84.5467
            },
            {
              "@type": "GeoCoordinates",
              "latitude": 29.7604,
              "longitude": -95.3698
            }
          ],
          "openingHours": "Mo-Fr 09:00-17:00",
          "serviceArea": [
            {
              "@type": "State",
              "name": "Michigan"
            },
            {
              "@type": "State", 
              "name": "Ohio"
            },
            {
              "@type": "State",
              "name": "Texas"
            },
            {
              "@type": "State",
              "name": "Illinois"
            },
            {
              "@type": "State",
              "name": "Indiana"
            }
          ],
          "hasOfferCatalog": {
            "@type": "OfferCatalog",
            "name": "AI & Technology Services",
            "itemListElement": [
              {
                "@type": "Offer",
                "itemOffered": {
                  "@type": "Service",
                  "name": "AI Automation",
                  "category": "Technology Services"
                }
              },
              {
                "@type": "Offer",
                "itemOffered": {
                  "@type": "Service",
                  "name": "Machine Learning",
                  "category": "Technology Services"
                }
              },
              {
                "@type": "Offer",
                "itemOffered": {
                  "@type": "Service",
                  "name": "Blockchain Development",
                  "category": "Technology Services"
                }
              }
            ]
          }
        };

      case 'faq':
        return {
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": data.faqs?.map((faq: any) => ({
            "@type": "Question",
            "name": faq.question,
            "acceptedAnswer": {
              "@type": "Answer",
              "text": faq.answer
            }
          })) || []
        };

      case 'service':
        return {
          "@context": "https://schema.org",
          "@type": "Service",
          "name": data.name || "AI Solutions",
          "description": data.description || "Comprehensive AI and technology solutions for businesses",
          "provider": {
            "@type": "Organization",
            "name": "Cognitive Craft Hub",
            "url": baseUrl
          },
          "areaServed": [
            {
              "@type": "State",
              "name": "Michigan"
            },
            {
              "@type": "State",
              "name": "Ohio"
            },
            {
              "@type": "State",
              "name": "Texas"
            },
            {
              "@type": "State",
              "name": "Illinois"
            },
            {
              "@type": "State",
              "name": "Indiana"
            }
          ],
          "serviceType": data.serviceType || "Technology Services",
          "offers": {
            "@type": "Offer",
            "description": data.offerDescription || "Professional AI and technology services"
          }
        };

      case 'product':
        return {
          "@context": "https://schema.org",
          "@type": "Product",
          "name": data.name || "Cognitive Care EHR",
          "description": data.description || "Scalable Electronic Health Records system for healthcare providers",
          "brand": {
            "@type": "Brand",
            "name": "Cognitive Craft Hub"
          },
          "manufacturer": {
            "@type": "Organization",
            "name": "Cognitive Craft Hub"
          },
          "category": data.category || "Healthcare Software",
          "releaseDate": data.releaseDate || "2026-03-01",
          "offers": {
            "@type": "Offer",
            "description": data.offerDescription || "Scalable EHR solution for all healthcare providers"
          }
        };

      case 'article':
        return {
          "@context": "https://schema.org",
          "@type": "Article",
          "headline": data.headline || "AI Solutions and Technology Insights",
          "description": data.description || "Expert insights on AI implementation and business transformation",
          "author": {
            "@type": "Organization",
            "name": "Cognitive Craft Hub"
          },
          "publisher": {
            "@type": "Organization",
            "name": "Cognitive Craft Hub",
            "logo": {
              "@type": "ImageObject",
              "url": `${baseUrl}/images/logo.png`
            }
          },
          "datePublished": data.datePublished || new Date().toISOString(),
          "dateModified": data.dateModified || new Date().toISOString(),
          "mainEntityOfPage": {
            "@type": "WebPage",
            "@id": window.location.href
          }
        };

      default:
        return {};
    }
  };

  const schemaMarkup = getSchemaMarkup();

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schemaMarkup, null, 2)}
      </script>
    </Helmet>
  );
};